import type { Placement, TowPlanResult, TowPlanNode, CurvedTowPath } from '../types';

/**
 * Represents an aircraft in the extraction order with its dependencies
 */
export interface ExtractionNode {
  /** Aircraft ID */
  aircraft_id: string;
  /** Aircraft model (make + model) */
  model: string;
  /** IDs of aircraft that must be extracted before this one */
  blockedBy: string[];
  /** Extraction order: 1 = first to extract, n = last, -1 = cycle detected */
  extractionOrder: number;
  /** Original placement index */
  placementIndex: number;
  /** Curved tow path with swept geometry (from backend) */
  curvedPath?: CurvedTowPath;
}

/**
 * Converts a TowPlanResult from the API to ExtractionNode array.
 *
 * The backend now computes blocking relationships using actual swept geometry
 * collision detection (curved tow paths). This function simply converts the
 * API response format to the frontend ExtractionNode format.
 *
 * @param towPlan - Tow plan result from the API
 * @param placements - Original placements array (for resolving blocker indices to IDs)
 * @returns Array of ExtractionNode sorted by extraction order
 */
export function convertTowPlanToExtractionOrder(
  towPlan: TowPlanResult,
  placements: Placement[]
): ExtractionNode[] {
  return towPlan.nodes.map(node => ({
    aircraft_id: node.aircraft_id,
    model: node.model,
    // Convert blocker indices to aircraft IDs
    blockedBy: node.blockedBy.map(idx => placements[idx]?.aircraft_id ?? `#${idx}`),
    extractionOrder: node.extractionOrder,
    placementIndex: node.placementIndex,
    curvedPath: node.curvedPath,
  }));
}

/**
 * Computes the extraction order for aircraft.
 *
 * If a towPlan is provided (from the API), uses the collision-based blocking
 * from swept geometry. Otherwise, falls back to simple LIFO placement order.
 *
 * @param placements - Array of aircraft placements
 * @param towPlan - Optional tow plan result from the API
 * @returns Array of ExtractionNode sorted by extraction order
 */
export function computeExtractionOrder(
  placements: Placement[],
  towPlan?: TowPlanResult
): ExtractionNode[] {
  if (placements.length === 0) {
    return [];
  }

  // If towPlan is provided, use the collision-based blocking from the API
  if (towPlan && towPlan.nodes.length > 0) {
    return convertTowPlanToExtractionOrder(towPlan, placements);
  }

  // Fallback to LIFO-based extraction order
  return computeLIFOExtractionOrder(placements);
}

/**
 * Computes extraction order using simple LIFO (Last-In-First-Out) placement order.
 *
 * This is a fallback when the API doesn't provide swept geometry blocking data.
 * In LIFO, aircraft placed later are extracted first.
 *
 * @param placements - Array of aircraft placements
 * @returns Array of ExtractionNode sorted by extraction order
 */
function computeLIFOExtractionOrder(placements: Placement[]): ExtractionNode[] {
  if (placements.length === 0) {
    return [];
  }

  // Use index as unique identifier since aircraft_id might not be unique per placement
  const placementMap = new Map<number, Placement>();
  for (let i = 0; i < placements.length; i++) {
    placementMap.set(i, placements[i]);
  }

  // Build dependency graph using indices
  // blockedByIndices[idx] = set of placement indices that block this placement
  // inDegree[idx] = number of placements that must be extracted first
  const blockedByIndices = new Map<number, Set<number>>();
  const inDegree = new Map<number, number>();

  // Initialize
  for (let i = 0; i < placements.length; i++) {
    blockedByIndices.set(i, new Set());
    inDegree.set(i, 0);
  }

  // Build edges from LIFO placement order
  // In LIFO, each aircraft is blocked by all aircraft placed AFTER it
  // (they must be extracted first so this one can exit)
  for (let i = 0; i < placements.length; i++) {
    for (let j = i + 1; j < placements.length; j++) {
      blockedByIndices.get(i)!.add(j);
    }
    inDegree.set(i, blockedByIndices.get(i)!.size);
  }

  // Kahn's algorithm with LIFO priority (higher index = placed later = extract first)
  const result: ExtractionNode[] = [];
  const processed = new Set<number>();

  // Process in rounds: find all with inDegree 0, sort by index descending (LIFO), process
  let order = 1;
  while (processed.size < placements.length) {
    // Find all unprocessed placements with inDegree 0
    const ready: number[] = [];
    for (let i = 0; i < placements.length; i++) {
      if (!processed.has(i) && inDegree.get(i) === 0) {
        ready.push(i);
      }
    }

    if (ready.length === 0) {
      // Cycle detected - remaining unprocessed are in cycle
      break;
    }

    // Sort by index DESCENDING (LIFO: last placed = first extracted)
    ready.sort((a, b) => b - a);

    for (const idx of ready) {
      processed.add(idx);
      const placement = placements[idx];

      // Convert blocker indices back to aircraft IDs for display
      const blockerIds = Array.from(blockedByIndices.get(idx) ?? [])
        .map(blockerIdx => placements[blockerIdx].aircraft_id);

      result.push({
        aircraft_id: placement.aircraft_id,
        model: `${placement.aircraft.make} ${placement.aircraft.model}`,
        blockedBy: blockerIds,
        extractionOrder: order++,
        placementIndex: idx,
      });

      // Decrement inDegree for placements blocked by this one
      for (let i = 0; i < placements.length; i++) {
        if (blockedByIndices.get(i)?.has(idx)) {
          const newDegree = inDegree.get(i)! - 1;
          inDegree.set(i, newDegree);
        }
      }
    }
  }

  // Any unprocessed placements are in a cycle
  for (let i = 0; i < placements.length; i++) {
    if (!processed.has(i)) {
      const placement = placements[i];
      const blockerIds = Array.from(blockedByIndices.get(i) ?? [])
        .map(blockerIdx => placements[blockerIdx].aircraft_id);

      result.push({
        aircraft_id: placement.aircraft_id,
        model: `${placement.aircraft.make} ${placement.aircraft.model}`,
        blockedBy: blockerIds,
        extractionOrder: -1, // Indicates cycle
        placementIndex: i,
      });
    }
  }

  return result;
}
